import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'src/devias/utils/axios';
import objFromArray from 'src/devias/utils/objFromArray';

interface ChatSliceState {
  activeThreadId: string | null;
  contacts: {
    byId: Record<string, any>;
    allIds: string[];
  };
  threads: {
    byId: Record<string, any>;
    allIds: string[];
  };
  participants: any[];
  recipients: any[];
}

const initialState: ChatSliceState = {
  activeThreadId: null,
  contacts: {
    byId: {},
    allIds: []
  },
  threads: {
    byId: {},
    allIds: []
  },
  participants: [],
  recipients: []
};

const slice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    getContacts(state, action: PayloadAction<{ contacts: any[] }>) {
      const { contacts } = action.payload;

      state.contacts.byId = objFromArray(contacts);
      state.contacts.allIds = Object.keys(state.contacts.byId);
    },
    getThreads(state, action: PayloadAction<{ threads: any[] }>) {
      const { threads } = action.payload;

      state.threads.byId = objFromArray(threads);
      state.threads.allIds = Object.keys(state.threads.byId);
    },
    getThread(state, action: PayloadAction<{ thread: any }>) {
      const { thread } = action.payload;

      if (thread) {
        state.threads.byId[thread.id] = thread;
        state.activeThreadId = thread.id;

        if (!state.threads.allIds.includes(thread.id)) {
          state.threads.allIds.push(thread.id);
        }
      } else {
        state.activeThreadId = null;
      }
    },
    markThreadAsSeen(state, action: PayloadAction<{ threadId: string }>) {
      const { threadId } = action.payload;
      const thread = state.threads.byId[threadId];

      if (thread) {
        thread.unreadCount = 0;
      }
    },
    resetActiveThread(state) {
      state.activeThreadId = null;
    },
    getParticipants(state, action: PayloadAction<{ participants: any[] }>) {
      const { participants } = action.payload;

      state.participants = participants;
    },
    addRecipient(state, action: PayloadAction<{ recipient: any }>) {
      const { recipient } = action.payload;
      const exists = state.recipients.find((_recipient) => _recipient.id === recipient.id);

      if (!exists) {
        state.recipients.push(recipient);
      }
    },
    removeRecipient(state, action: PayloadAction<{ recipientId: string }>) {
      const { recipientId } = action.payload;

      state.recipients = state.recipients.filter((recipient) => recipient.id !== recipientId);
    }
  }
});

export const reducer = slice.reducer;

export const getContacts = () => async (dispatch: any) => {
  const response = await axios.get('/api/chat/contacts');

  dispatch(slice.actions.getContacts(response.data));
};

export const getThreads = () => async (dispatch: any) => {
  const response = await axios.get('/api/chat/threads');

  dispatch(slice.actions.getThreads(response.data));
};

export const getThread = (threadKey: string) => async (dispatch: any) => {
  const response = await axios.get('/api/chat/thread', {
    params: {
      threadKey
    }
  });

  dispatch(slice.actions.getThread(response.data));
};

export const markThreadAsSeen = (threadId: string) => async (dispatch: any) => {
  await axios.get('/api/chat/thread/mark-as-seen', {
    params: {
      threadId
    }
  });

  dispatch(slice.actions.markThreadAsSeen({ threadId }));
};

export const resetActiveThread = () => (dispatch: any) => {
  dispatch(slice.actions.resetActiveThread());
};

export const getParticipants = (threadKey: string) => async (dispatch: any) => {
  const response = await axios.get('/api/chat/participants', {
    params: {
      threadKey
    }
  });

  dispatch(slice.actions.getParticipants(response.data));
};

export const addRecipient = (recipient: any) => (dispatch: any) => {
  dispatch(slice.actions.addRecipient({ recipient }));
};

export const removeRecipient = (recipientId: string) => (dispatch: any) => {
  dispatch(slice.actions.removeRecipient({ recipientId }));
};

export default slice;
